import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormPrice,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SendIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { z } from "zod";
import { refundsApi, type EligiblePackageDTO } from "../api/client";
import { useCreateRefundRequestMutation } from "../api/queries";

type CreateRefundRequestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const ELIGIBLE_QUERY_KEY = ["refunds", "eligible-packages"] as const;

const CreateRefundRequestFormSchema = z
  .object({
    packageValue: z.string().min(1, "ກະລຸນາເລືອກແພັກເກດ"),
    originalPrice: z.number().int().min(0).optional(),
    refundAmount: z.number().int().min(1, "ກະລຸນາປ້ອນຈຳນວນເງິນທີ່ຖືກຕ້ອງ"),
    reason: z.string().min(1, "ກະລຸນາປ້ອນເຫດຜົນໃນການຂໍຄືນເງິນ"),
  })
  .refine(
    (data) => {
      if (data.originalPrice == null) return true;
      return data.refundAmount <= data.originalPrice;
    },
    {
      message: "ຈຳນວນເງິນທີ່ຄືນບໍ່ສາມາດເກີນລາຄາຕົ້ນສະບັບ",
      path: ["refundAmount"],
    },
  );

type CreateRefundRequestFormValues = z.infer<
  typeof CreateRefundRequestFormSchema
>;

export function CreateRefundRequestDialog({
  open,
  onOpenChange,
}: CreateRefundRequestDialogProps) {
  const nav = useNavigate();
  const createMutation = useCreateRefundRequestMutation();

  const methods = RHF.useForm<CreateRefundRequestFormValues>({
    resolver: zodResolver(CreateRefundRequestFormSchema),
    defaultValues: {
      packageValue: "",
      originalPrice: undefined,
      refundAmount: 0,
      reason: "",
    },
  });

  const packageValue = methods.watch("packageValue");
  const originalPrice = methods.watch("originalPrice");

  const queryFn = useCallback(
    async (params: {
      search: string;
      pageParam: number;
    }): Promise<{ items: EligiblePackageDTO[]; nextPage: number | null }> => {
      const result = await refundsApi.eligiblePackages.list({
        search: params.search || undefined,
        limit: 20,
        pageParam: params.pageParam,
      });
      return result;
    },
    [],
  );

  const preloadQueryFn = useCallback(
    async (value: string): Promise<EligiblePackageDTO | null> => {
      return refundsApi.eligiblePackages.preload(value);
    },
    [],
  );

  useEffect(() => {
    if (!packageValue) {
      methods.setValue("originalPrice", undefined);
      methods.setValue("refundAmount", 0);
      return;
    }
    refundsApi.eligiblePackages.preload(packageValue).then((pkg) => {
      if (pkg) {
        methods.setValue("originalPrice", pkg.originalPrice);
        methods.setValue("refundAmount", pkg.originalPrice);
      } else {
        methods.setValue("originalPrice", undefined);
        methods.setValue("refundAmount", 0);
      }
    });
  }, [packageValue, methods]);

  const handleOpenChange = (next: boolean) => {
    if (!next) methods.reset();
    onOpenChange(next);
  };

  const handleSubmit = async (values: CreateRefundRequestFormValues) => {
    const [targetType, id] = values.packageValue.split(":");
    if (!targetType || !id) return;

    try {
      const result = await createMutation.mutateAsync({
        targetType:
          targetType === "package" ? "package" : "private_lesson_package",
        studentPackageId: targetType === "package" ? id : undefined,
        privateLessonPackageId:
          targetType === "private_lesson_package" ? id : undefined,
        reason: values.reason.trim(),
        refundAmount: values.refundAmount,
      });
      methods.reset();
      handleOpenChange(false);
      nav({ to: "/app/refunds/$id", params: { id: result.id } });
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        onPointerDownOutside={(e) => {
          if (createMutation.isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (createMutation.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>ສ້າງຄຳຮ້ອງຂໍຄືນເງິນ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            ກະລຸນາປ້ອນລາຍລະອຽດຂ້າງລຸ່ມນີ້ ເພື່ອຮ້ອງຂໍຄືນເງິນ
          </p>
        </DialogHeader>

        <FormRoot<CreateRefundRequestFormValues>
          methods={methods}
          onSubmit={handleSubmit}
        >
          <div className="space-y-4 py-4">
            <FormInfiniteCombobox<EligiblePackageDTO>
              name="packageValue"
              label="ເລືອກແພັກເກດ"
              requiredMark
              queryKey={[...ELIGIBLE_QUERY_KEY]}
              queryFn={queryFn}
              preloadQueryFn={preloadQueryFn}
              getLabel={(item) =>
                `${item.studentName} (${item.memberId}) - ${item.packageName}`
              }
              getValue={(item) => item.value}
              placeholder="ເລືອກແພັກເກດນັກຮຽນ ຫຼື ບົດຮຽນສ່ວນຕົວ"
              clearable
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ລາຄາຕົ້ນສະບັບ</Label>
                <Input
                  value={
                    originalPrice != null
                      ? originalPrice.toLocaleString("lo-LA")
                      : "-"
                  }
                  readOnly
                  disabled
                  className="bg-muted"
                />
                <span className="text-muted-foreground text-xs">ກີບ</span>
              </div>
              <FormPrice
                name="refundAmount"
                label="ຈຳນວນເງິນທີ່ຈະຄືນ"
                placeholder="0"
                currencySymbol="₭"
                requiredMark
              />
            </div>

            <FormTextarea
              name="reason"
              label="ເຫດຜົນໃນການຂໍຄືນເງິນ"
              placeholder="ອະທິບາຍວ່າເປັນຫຍັງຈຶ່ງຮ້ອງຂໍຄືນເງິນ..."
              rows={4}
              requiredMark
              className="resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              <SendIcon className="mr-2 size-4" />
              ສົ່ງຄຳຮ້ອງຂໍ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
